import { type DocumentNode, gql } from '@apollo/client';
import { jsonToGraphQLQuery, VariableType } from 'json-to-graphql-query';
import type { FieldValue } from './types';

interface SuperViewSimpleFields {
  sequence: FieldValue;
  label: FieldValue;
  slug: FieldValue;
  value: FieldValue;
  fieldType: FieldValue;
  fallbackMessage: FieldValue;
  dateDisplayStyle: FieldValue;
  // isSystemField is needed to safely detect the _akomi_augments sentinel row
  // alongside its reserved slug (defense against a future FieldDef collision).
  isSystemField: FieldValue;
}

interface SuperViewFullFields extends SuperViewSimpleFields {
  isEditable: FieldValue;
  choices: FieldValue;
  metadataFieldId: FieldValue;
  fieldDefId: FieldValue;
}

interface SuperViewSimpleQueryResult {
  query: {
    __variables: {
      globalId: 'String!';
      viewComponentName: 'String!';
    };
    superView: SuperViewSimpleFields & {
      __args: {
        globalId: VariableType;
        viewComponentName: VariableType;
      };
    };
  };
}

interface SuperViewFullQueryResult {
  query: {
    __variables: {
      globalId: 'String!';
      viewComponentName: 'String!';
    };
    superView: SuperViewFullFields & {
      __args: {
        globalId: VariableType;
        viewComponentName: VariableType;
      };
    };
  };
}

interface SharedSuperViewQueryResult {
  query: {
    __variables: {
      globalId: 'String!';
      viewComponentName: 'String!';
    };
    sharedSuperView: SuperViewSimpleFields & {
      __args: {
        globalId: VariableType;
        viewComponentName: VariableType;
      };
    };
  };
}

export const superViewSimple = (truify: boolean = false): SuperViewSimpleQueryResult => ({
  query: {
    __variables: {
      globalId: 'String!',
      viewComponentName: 'String!',
    },
    superView: {
      __args: {
        globalId: new VariableType('globalId'),
        viewComponentName: new VariableType('viewComponentName'),
      },
      sequence: truify,
      label: truify,
      slug: truify,
      value: truify,
      fieldType: truify,
      fallbackMessage: truify,
      dateDisplayStyle: truify,
      isSystemField: truify,
    },
  },
});

export const superViewFull = (truify: boolean = false): SuperViewFullQueryResult => ({
  query: {
    __variables: {
      globalId: 'String!',
      viewComponentName: 'String!',
    },
    superView: {
      __args: {
        globalId: new VariableType('globalId'),
        viewComponentName: new VariableType('viewComponentName'),
      },
      sequence: truify,
      label: truify,
      value: truify,
      fieldType: truify,
      fallbackMessage: truify,
      dateDisplayStyle: truify,
      slug: truify,
      isEditable: truify,
      isSystemField: truify,
      choices: truify,
      metadataFieldId: truify,
      fieldDefId: truify,
    },
  },
});

export const buildSuperView = (): DocumentNode => {
  const query = superViewSimple(true);
  const gQuery = jsonToGraphQLQuery(query, { pretty: true });
  return gql(gQuery);
};

export const buildSuperViewEditQuery = (): DocumentNode => {
  const query = superViewFull(true);
  const gQuery = jsonToGraphQLQuery(query, { pretty: true });
  return gql(gQuery);
};

export const buildSharedSuperView = (): DocumentNode => {
  const query = superViewSimple(true);
  const modifiedQuery: SharedSuperViewQueryResult = {
    query: {
      __variables: query.query.__variables,
      sharedSuperView: {
        ...query.query.superView,
      },
    },
  };
  const gQuery = jsonToGraphQLQuery(modifiedQuery, { pretty: true });
  return gql(gQuery);
};
